'use client';

import React, { useState, useEffect } from 'react';
import { useTranslations } from 'next-intl';
import { useRouter } from 'next/navigation';
import { Link } from '@/i18n/navigation';
import { HeaderProps } from './types';
import { Logo, CitySelector, UserMenu, GuestLinks, BackButton } from './components';
import { MobileHeader } from './MobileHeader';
import { Button } from '../ui/button';
import { cn } from '@/lib/utils';
import { useUser } from '@/context/userContext';
import { Open_Sans } from 'next/font/google';

const openSans = Open_Sans({ subsets: ['latin'], weight: ['600'] });

export const Header: React.FC<HeaderProps> = ({
  transparent = false,
  logoVariant = 'default',
  showUserMenu = false,
  showGuestLinks = false,
  showCitySelector = false,
  showMobileNav = true,
  hideMobileMenu = false,
  showBackButton = false,
  onBackClick,
  showCenterNav = false,
  centerNavText = 'Para restaurantes',
  customLinks,
  rightCustomLinks,
  fixed = false,
  className,
  authenticatedLeftAction,
  authenticatedRightAction,
  authenticatedTitle,
  mobileTitle,
}) => {
  const [isMobile, setIsMobile] = useState(false);
  const t = useTranslations('Header');
  const router = useRouter();
  const { user } = useUser();

  // Check if user is authenticated
  const isAuthenticated = !!user;

  // Detect mobile device
  useEffect(() => {
    const checkIfMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };

    checkIfMobile();
    window.addEventListener('resize', checkIfMobile);
    return () => window.removeEventListener('resize', checkIfMobile);
  }, []);

  const headerClasses = cn(
    'px-4 sm:px-6 md:px-8 lg:px-12 xl:px-16 2xl:px-20',
    {
      'bg-transparent': transparent,
      'fixed top-0 left-0 right-0 z-50': fixed,
    },
    className,
  );

  const textColorClass = logoVariant === 'white' ? 'text-white' : 'text-[#393941]';
  const hoverTextClass = logoVariant === 'white' ? 'hover:text-gray-200' : 'hover:text-[#2f2f36]';

  return (
    <nav className={headerClasses}>
      <header className='flex flex-col  max-w-screen-2xl mx-auto'>
        <div className='flex py-0.5 sm:py-1 md:py-1 lg:py-1 justify-between items-center'>
          <div className='flex items-center gap-4'>
            {showBackButton ? (
              <BackButton
                variant={logoVariant}
                onClick={() => {
                  router.back();
                }}
              />
            ) : (
              <div className='flex items-center gap-1'>
                <Logo variant={logoVariant} />
                <Link href='/' className='transition-opacity hover:opacity-90'>
                  <span
                    className={cn(
                      openSans.className,
                      'hidden text-lg sm:text-xl md:text-2xl font-semibold select-none text-[#181818]',
                      hoverTextClass,
                    )}
                  >
                    mangia
                  </span>
                </Link>
              </div>
            )}

            {showCitySelector && <CitySelector className='hidden sm:block' />}

            {/* Authenticated Left Action */}
            {isAuthenticated && authenticatedLeftAction && (
              <div className='hidden sm:block'>{authenticatedLeftAction}</div>
            )}
          </div>

          {/* Mobile Title - Perfectly Centered */}
          {mobileTitle && (
            <div className='flex md:hidden items-center justify-between w-full absolute left-0 px-6'>
              {/* Left Spacer - Same width as logo section */}
              <div className='flex items-center gap-4 opacity-0 pointer-events-none'>
                <div className='w-10 h-10' />
                {showCitySelector && <CitySelector className='hidden sm:block' />}
                {isAuthenticated && authenticatedLeftAction && (
                  <div className='hidden sm:block'>{authenticatedLeftAction}</div>
                )}
              </div>

              {/* Centered Title */}
              <div className='flex-1 flex justify-center'>
                <h1 className='text-lg text-black font-semibold truncate px-4'>{mobileTitle}</h1>
              </div>

              {/* Right Spacer - Same width as right section */}
              <div className='flex items-center opacity-0 pointer-events-none'>
                {isAuthenticated && authenticatedRightAction && (
                  <div className='mr-2'>{authenticatedRightAction}</div>
                )}
                <div className='w-10 h-10' /> {/* MobileHeader button size */}
              </div>
            </div>
          )}

          {/* Desktop Center Navigation - Custom Links */}
          <div className='hidden md:flex items-center justify-between w-full'>
            {(showCenterNav ||
              (customLinks && !isAuthenticated) ||
              (isAuthenticated && authenticatedTitle)) && (
              <div className='flex items-center justify-center absolute left-1/2 transform -translate-x-1/2'>
                {isAuthenticated && authenticatedTitle ? (
                  <h1 className={cn('text-lg font-semibold', textColorClass)}>
                    {authenticatedTitle}
                  </h1>
                ) : customLinks && !isAuthenticated ? (
                  <div className='flex items-center space-x-8'>
                    {customLinks.map((link, index) => (
                      <Link key={index} href={link.href}>
                        <button
                          className={cn(
                            'text-sm font-medium cursor-pointer transition-colors bg-transparent border-none focus:outline-none px-3 md:px-4',
                            textColorClass,
                            hoverTextClass,
                          )}
                        >
                          {link.label}
                        </button>
                      </Link>
                    ))}
                  </div>
                ) : (
                  showCenterNav && (
                    <button
                      className={cn(
                        'text-md font-medium cursor-pointer transition-colors bg-transparent border-none focus:outline-none px-3 md:px-4',
                        textColorClass,
                        hoverTextClass,
                      )}
                    >
                      {centerNavText}
                    </button>
                  )
                )}
              </div>
            )}

            {/* Desktop Right Section */}
            <div className='hidden md:flex items-center space-x-4 ml-auto'>
              {/* Authenticated Right Action */}
              {isAuthenticated && authenticatedRightAction && (
                <div className='mr-4'>{authenticatedRightAction}</div>
              )}

              {/* Right Custom Links */}
              {rightCustomLinks && !isAuthenticated && (
                <div className='flex items-center space-x-4 mr-4'>
                  {rightCustomLinks.map((link, index) => (
                    <Link key={index} href={link.href}>
                      {link.isTextButton ? (
                        <button
                          className={cn(
                            'text-sm font-medium cursor-pointer transition-colors bg-transparent border-none focus:outline-none',
                            textColorClass,
                            hoverTextClass,
                          )}
                        >
                          {link.label}
                        </button>
                      ) : (
                        <Button
                          variant={link.variant || 'secondary'}
                          size='lg'
                          className={cn(
                            'transition-colors rounded-full',
                            link.variant === 'secondary' &&
                              'bg-white text-gray-900 hover:bg-gray-100',
                            link.variant === 'ghost' && cn(textColorClass, hoverTextClass),
                          )}
                        >
                          {link.label}
                        </Button>
                      )}
                    </Link>
                  ))}
                </div>
              )}

              {/* Default Guest Links (only if rightCustomLinks not provided) */}
              {showGuestLinks && !isAuthenticated && !rightCustomLinks && (
                <GuestLinks className='mr-4' />
              )}

              {showUserMenu && <UserMenu />}
              <MobileHeader logoVariant={logoVariant} />
            </div>
          </div>

          {/* Mobile Navigation */}
          {showMobileNav && !hideMobileMenu && (
            <div className='flex items-center justify-end md:hidden relative z-10'>
              {/* Mobile Authenticated Right Action */}
              {isAuthenticated && authenticatedRightAction && (
                <div className='mr-2'>{authenticatedRightAction}</div>
              )}

              <MobileHeader logoVariant={logoVariant} />
            </div>
          )}
        </div>
      </header>
    </nav>
  );
};

// Export types for external use
export type { HeaderProps } from './types';
